import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import ShoppingRequestChat from '@/components/ShoppingRequestChat/ShoppingRequestChat';
import { apiService } from '@/services/api';
import { isValidUUID } from '@/utils/uuid';

/**
 * Standalone page for /chat/shopping-request/:requestId (share link or deep link).
 * Renders ShoppingRequestChat and fetches request/branch details for labels.
 */
export default function ShoppingRequestChatPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const branchId = searchParams.get('branchId') ?? undefined;

  const [branchName, setBranchName] = useState<string>('Branch');
  const [shopName, setShopName] = useState<string>('Store');
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = requestId?.trim();
    if (!id || !isValidUUID(id)) {
      setError('Invalid request.');
      setLoading(false);
      return;
    }

    apiService
      .getShoppingRequest(id)
      .then((res: any) => {
        const raw = res?.data ?? res;
        if (!raw?.id) {
          setError('Request not found.');
          setLoading(false);
          return;
        }
        const branch = raw.branch;
        setBranchName(branch?.name ?? 'Branch');
        setShopName(branch?.name ?? raw.organizer?.name ?? 'Store');
        setStatus(raw.status ?? null);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load request.');
        setLoading(false);
      });
  }, [requestId]);

  const handleClose = () => {
    if (branchId) {
      navigate(`/customer/bookings`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const id = requestId?.trim();
  if (!id || !isValidUUID(id)) {
    return (
      <>
        <DashboardHeader />
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
          <button type="button" onClick={() => navigate(-1)} style={{ padding: '0.5rem 0', marginBottom: '1rem', fontSize: '0.9375rem', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}>
            ← Back
          </button>
          <p>{error ?? 'Invalid request.'}</p>
          <button type="button" onClick={() => navigate('/customer/bookings')} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Back to Bookings
          </button>
        </div>
        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <DashboardHeader />
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ padding: '0.5rem 0', marginBottom: '1rem', fontSize: '0.9375rem', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          >
            ← Back
          </button>
          <p>Loading chat...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <DashboardHeader />
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
          <button type="button" onClick={() => navigate(-1)} style={{ padding: '0.5rem 0', marginBottom: '1rem', fontSize: '0.9375rem', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}>
            ← Back
          </button>
          <p>{error}</p>
          <button type="button" onClick={() => navigate('/customer/bookings')} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Back to Bookings
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <DashboardHeader />
      <div style={{ padding: '1rem 1.5rem 0', maxWidth: 900, margin: '0 auto' }}>
        <button
          type="button"
          onClick={handleClose}
          style={{
            padding: '0.5rem 0',
            marginBottom: '0.5rem',
            fontSize: '0.9375rem',
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          ← Back
        </button>
      </div>
      <ShoppingRequestChat
        requestId={id}
        branchName={branchName}
        shopName={shopName}
        onClose={handleClose}
        initialRequestStatus={status}
      />
      <Footer />
    </>
  );
}
